import { useState, useCallback } from 'react';
import type React from 'react';
import type { MapNote, NoteIcon } from '../types';
import type { UseMultiplayerReturn } from './useMultiplayer';

export interface UseNotesReturn {
  notes: MapNote[];
  setNotes: React.Dispatch<React.SetStateAction<MapNote[]>>;
  createNote: (pageId: string, x: number, y: number, title: string, content: string, icon: NoteIcon, color: string, visibility: 'gm' | 'all', parentNoteId?: string | null) => void;
  updateNote: (pageId: string, noteId: string, changes: Partial<Pick<MapNote, 'title' | 'content' | 'icon' | 'color' | 'visibility' | 'parentNoteId'>>) => void;
  deleteNote: (pageId: string, noteId: string) => void;
  moveNote: (pageId: string, noteId: string, x: number, y: number) => void;
  setParent: (pageId: string, noteId: string, parentNoteId: string | null) => void;
}

export function useNotes(mp: UseMultiplayerReturn): UseNotesReturn {
  const [notes, setNotes] = useState<MapNote[]>([]);

  const createNote = useCallback((
    pageId: string, x: number, y: number, title: string, content: string,
    icon: NoteIcon, color: string, visibility: 'gm' | 'all', parentNoteId?: string | null,
  ) => {
    mp.send({ type: 'note:create', pageId, x, y, title, content, icon, color, visibility, parentNoteId: parentNoteId ?? null });
  }, [mp]);

  const updateNote = useCallback((
    pageId: string, noteId: string,
    changes: Partial<Pick<MapNote, 'title' | 'content' | 'icon' | 'color' | 'visibility' | 'parentNoteId'>>,
  ) => {
    mp.send({ type: 'note:update', pageId, noteId, changes });
  }, [mp]);

  const deleteNote = useCallback((pageId: string, noteId: string) => {
    mp.send({ type: 'note:delete', pageId, noteId });
  }, [mp]);

  const moveNote = useCallback((pageId: string, noteId: string, x: number, y: number) => {
    mp.send({ type: 'note:move', pageId, noteId, x, y });
  }, [mp]);

  const setParent = useCallback((pageId: string, noteId: string, parentNoteId: string | null) => {
    mp.send({ type: 'note:set-parent', pageId, noteId, parentNoteId });
  }, [mp]);

  return { notes, setNotes, createNote, updateNote, deleteNote, moveNote, setParent };
}
